import { createAsyncThunk } from '@reduxjs/toolkit';
import { getToken } from '../../Utils/authUtils';
import { HL7 } from '../../constant';

interface Payload {
  _id: string;
  firstName: string;
}

/**
 * Download the report as RTF — the same document the HL7 distribution embeds.
 *
 * Served by the backend rather than built here: the RTF has to be byte-identical to what
 * a practice's software receives, and that renderer lives in `reportRtfService`.
 */
export const downloadRtf = createAsyncThunk(
  'Download RTF',
  async (payload: Payload, { rejectWithValue }) => {
    try {
      const res = await fetch(`${HL7.RTF(payload._id)}?download=1`, {
        method: 'GET',
        headers: { Authorization: 'Bearer ' + getToken() },
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        return rejectWithValue(errorData.msg || 'Download RTF failed.');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (payload.firstName || 'report').replace(/[^a-zA-Z0-9]/g, '') || 'report';
      link.download = `${safeName}.rtf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { msg: 'RTF downloaded successfully.' };
    } catch (error) {
      return rejectWithValue(error.message || 'An error occurred while downloading the RTF.');
    }
  }
);
